import colors from '@allesauto/design/tokens/colors.json';
import { consts } from '@allesauto/utils/consts';
import { parseFormattedNumber } from '@allesauto/utils/funcs';
import clsx from 'clsx';
import Slider, { SliderProps } from 'rc-slider';
import React, {
  FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import './Slider.css';

import InputField, { InputFieldProps } from '../inputField';
import { useWhiteLabel } from '../whiteLabel';

export interface ISliderProps {
  id?: string;
  darkBg?: boolean;
  mediumBg?: boolean;
  range?: boolean;
  rangeProps?: SliderProps;
  min: number;
  max: number;
  step?: number;
  value: number[];
  tooltipFormatter?: (value: number | number[] | null) => string | null;
  inputValueFormatter?: (value: number | string) => string;
  useFloatValues?: boolean;
  rangeLabelText?: string | null;
  input1Props?: InputFieldProps;
  input2Props?: InputFieldProps;
  disabled?: boolean;
  onChange?: (value: number | number[]) => void;
  onBeforeChange?: (value: number | number[]) => void;
  onAfterChange?: (value: number | number[]) => void;
}

const isTouchDevice = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    ('ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.maxTouchPoints > 0)
  );
};

const AASlider: React.FC<ISliderProps> = ({
  id = '',
  darkBg = false,
  mediumBg = false,
  range,
  rangeProps,
  min,
  max,
  step,
  value = [min, max],
  useFloatValues = false,
  rangeLabelText,
  tooltipFormatter = (value) => (value !== null ? value.toString() : null),
  input1Props,
  input2Props,
  disabled,
  ...restProps
}: ISliderProps): JSX.Element | null => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const formatInputValue = (val: string | number | null): number => {
    if (val === null) return 0;

    return useFloatValues
      ? parseFloat(val as string)
      : parseInt(val as string, 10);
  };

  const intl = useIntl();

  const isWhiteLabel = Boolean(useWhiteLabel());

  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const input1Ref = useRef(null);
  const input2Ref = useRef(null);

  const [input1Active, setInput1Active] = useState<boolean>(false);
  const [input2Active, setInput2Active] = useState<boolean>(false);

  const [input1Value, setInput1Value] = useState<number | string>(
    isNaN(value[0]) || value[0] < min ? min : value[0]
  );
  const [input2Value, setInput2Value] = useState<number | string>(
    isNaN(value[1]) || value[1] > max ? max : value[1]
  );

  const [sliderValue, setSliderValue] = useState<number | number[] | null>(
    range
      ? [formatInputValue(input1Value), formatInputValue(input2Value)]
      : formatInputValue(input1Value)
  );

  const [isDragging, setIsDragging] = useState<boolean>(false);

  const getBackgroundColorFirstHandle = useCallback(() => {
    if (darkBg && !isWhiteLabel) return colors.purple80;
    if (darkBg && isWhiteLabel) return colors.grey90;
    if (!darkBg && !isWhiteLabel) return colors.white;
    if (mediumBg) return colors.grey10;
    return colors.white;
  }, [darkBg, mediumBg, isWhiteLabel]);

  const getBackgroundColorSecondHandle = useCallback(() => {
    if (darkBg && !isWhiteLabel) return colors.green60;
    if (darkBg && isWhiteLabel) return colors.white;
    if (!darkBg && isWhiteLabel) return colors.black;
    // if (mediumBg) return colors.purple80;
    return colors.purple80;
  }, [darkBg, mediumBg, isWhiteLabel]);

  const startHandleStyles = {
    background: getBackgroundColorFirstHandle(),
  };

  const endHandleStyles = {
    background: getBackgroundColorSecondHandle(),
  };

  useEffect(() => {
    setInput1Value(isNaN(value[0]) || value[0] < min ? min : value[0]);
    setInput2Value(isNaN(value[1]) || value[1] > max ? max : value[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max]);

  useEffect(() => {
    if (range) {
      setSliderValue([
        formatInputValue(input1Value),
        formatInputValue(input2Value),
      ]);
    } else {
      setSliderValue(formatInputValue(input1Value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input1Value, input2Value]);

  const onInputChange = (): void => {
    if (input1Ref && input1Ref.current && input2Ref && input2Ref.current) {
      const val1 = formatInputValue(
        parseFormattedNumber(
          intl,
          (input1Ref.current as HTMLInputElement).value
        )
      );
      setInput1Value(val1);

      const val2 = formatInputValue(
        parseFormattedNumber(
          intl,
          (input2Ref.current as HTMLInputElement).value
        )
      );
      setInput2Value(val2);
    }
  };

  const enforceMinRange = (e: FocusEvent<HTMLInputElement>) => {
    const value = Math.max(
      min,
      Math.min(
        Number(input2Value),
        Number((e.target as HTMLInputElement).value)
      )
    );

    value !== input1Value && setInput1Value(value);
    // call onChange after value is enforced to be in range
    rangeProps?.onChange &&
      rangeProps.onChange([Number(value), Number(input2Value)]);
  };

  const enforceMaxRange = (e: FocusEvent<HTMLInputElement>) => {
    const value = Math.max(
      Number(input1Value),
      Math.min(max, Number((e.target as HTMLInputElement).value))
    );
    value !== input2Value && setInput2Value(value);
    // call onChange after value is enforced to be in range
    rangeProps?.onChange &&
      rangeProps.onChange([Number(input1Value), Number(value)]);
  };

  const onChangeInternal = useCallback(
    (eventValue: number | number[]): void => {
      if (range && Array.isArray(eventValue) && Array.isArray(sliderValue)) {
        setInput1Active(eventValue[0] !== sliderValue[0]);
        setInput2Active(eventValue[1] !== sliderValue[1]);
      }

      if (Array.isArray(eventValue)) {
        setInput1Value(eventValue[0]);
        setInput2Value(eventValue[1]);
      } else {
        setInput1Value(eventValue);
      }

      restProps.onChange &&
        restProps.onChange(
          Array.isArray(eventValue) ? eventValue[0] : eventValue
        );
      rangeProps?.onChange &&
        Array.isArray(eventValue) &&
        rangeProps?.onChange(eventValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [value]
  );

  const onBeforeChangeInternal = useCallback(
    (eventValue: number | number[]): void => {
      isTouchDevice() && setIsDragging(true);
      restProps.onBeforeChange &&
        restProps.onBeforeChange(
          Array.isArray(eventValue) ? eventValue[0] : eventValue
        );
      rangeProps?.onBeforeChange &&
        Array.isArray(eventValue) &&
        rangeProps?.onBeforeChange(eventValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [value]
  );

  const onAfterChangeInternal = useCallback(
    (eventValue: number | number[]): void => {
      setInput1Active(false);
      setInput2Active(false);
      setIsDragging(false);
      restProps.onAfterChange &&
        restProps.onAfterChange(
          Array.isArray(eventValue) ? eventValue[0] : eventValue
        );
      rangeProps?.onAfterChange &&
        Array.isArray(eventValue) &&
        rangeProps?.onAfterChange(eventValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [value]
  );

  useEffect(() => {
    if (tooltipRef && tooltipRef.current) {
      const tt = tooltipRef.current;

      if (sliderValue !== null) {
        const tmp = Array.isArray(sliderValue) ? sliderValue[0] : sliderValue;
        let position = ((tmp - min) * 100) / (max - min);
        if (position > 100) position = 100;
        if (position < 0) position = 0;
        let offset = 0;

        if (containerRef && containerRef.current) {
          tt.style.left = position + '%';
          tt.classList.remove('HasOverflowLeft');
          tt.classList.remove('HasOverflowRight');

          const ttBounds = tt.getBoundingClientRect();
          const containerBounds = containerRef.current.getBoundingClientRect();

          const ttX = ttBounds.x - containerBounds.x;

          if (ttX + ttBounds.width > containerBounds.width) {
            offset = -ttBounds.width / 2 + 7;
            tt.classList.add('HasOverflowRight');
          } else if (ttX - ttBounds.width < 0) {
            offset = ttBounds.width / 2 - 7;
            tt.classList.add('HasOverflowLeft');
          } else {
            offset = 0;
            tt.classList.remove('HasOverflowLeft');
            tt.classList.remove('HasOverflowRight');
          }
        }

        tt.style.left = `calc(${position}% + ${offset}px)`;
      }
    }
  }, [sliderValue, max, min, containerRef, tooltipRef, hasMounted]);

  const tooltipClasses = clsx(
    'Tooltip',
    'text-p1Regular z-40',
    darkBg && 'bg-transparent text-green60 monochrome:text-white',
    !darkBg && 'text-purple80 monochrome:text-grey90',
    sliderValue !== null ? 'block' : 'hidden',
    isDragging &&
      'IsDragging sm:p-15px rounded rounded-default monochrome:text-grey90',
    !isDragging && 'p-0 pt-15px font-headline font-weight-bold',
    !darkBg &&
      isDragging &&
      'sm:bg-grey80 sm:text-white sm:monochrome:bg-grey80 sm:monochrome:text-white',
    darkBg &&
      isDragging &&
      'sm:text-grey80 sm:bg-white sm:monochrome:bg-white sm:monochrome:text-grey90'
  );

  const formatNumber = (value: string | number) => {
    if (isNaN(parseFloat(value.toString()))) return '';

    return intl.formatNumber(
      useFloatValues ? parseFloat(value.toString()) : parseInt(value.toString())
    );
  };

  // Slider skeleton as a placeholder of the slider on SSR
  const SliderSkeleton = (maxValue: number) => (
    <>
      <div className="rc-slider rc-slider-horizontal">
        <div className="rc-slider-rail"></div>
        <div className="left-0 rc-slider-track w-full"></div>
        <div className="rc-slider-step"></div>
        <div className="!bg-purple80 left-full rc-slider-handle translate-x-[-50%] wl-slider"></div>
      </div>
      <span className="!left-[calc(100%-30px)] HasOverflowRight Tooltip block font-headline font-weight-bold p-0 pt-15px text-p1 text-purple80 z-40 monochrome:text-grey90">
        {formatNumber(maxValue)} {consts.CURRENCIES.EUR.SHORT}
      </span>
    </>
  );

  return (
    <div
      ref={containerRef}
      className={clsx(
        'Slider py-20px relative',
        darkBg && 'HasDarkBg',
        range && 'HasInputs',
        isDragging && 'IsDragging',
        disabled && 'IsDisabled',
        isTouchDevice() && 'IsTouchDevice'
      )}
      data-tracking-id={`${id}--slider`}
    >
      {hasMounted ? (
        <>
          {!range && (
            <>
              <Slider
                {...restProps}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                value={formatInputValue(input1Value)}
                handleStyle={range ? startHandleStyles : endHandleStyles}
                onChange={onChangeInternal}
                onBeforeChange={onBeforeChangeInternal}
                onAfterChange={onAfterChangeInternal}
              />
              <span ref={tooltipRef} className={tooltipClasses}>
                {tooltipFormatter(sliderValue)}
              </span>
            </>
          )}
          {range && (
            <>
              <Slider
                {...rangeProps}
                range
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                allowCross={false}
                pushable={true}
                value={[
                  formatInputValue(input1Value),
                  formatInputValue(input2Value),
                ]}
                onChange={onChangeInternal}
                onBeforeChange={onBeforeChangeInternal}
                onAfterChange={onAfterChangeInternal}
                handleStyle={[startHandleStyles, endHandleStyles]}
              />
              <div className="InputWrapper">
                <div className="HasMaxWidth InputFieldWrapper">
                  <InputField
                    id={`${id}-min`}
                    name={`${id}-min`}
                    darkBg={darkBg}
                    type="number"
                    step={step}
                    disabled={disabled}
                    min={min}
                    onBlur={(e) => enforceMinRange(e)}
                    value={formatNumber(
                      input1Value !== null ? input1Value : min
                    )}
                    {...input1Props}
                    onChange={onInputChange}
                    ref={input1Ref}
                    highlightInput={input1Active}
                    useHeadlineFont={true}
                  />
                </div>
                {rangeLabelText && (
                  <span
                    className={clsx(
                      darkBg
                        ? 'text-white'
                        : 'text-purple80 monochrome:text-black',
                      'RangeLabel flex-auto leading-5 min-w-0 px-20px text-center'
                    )}
                  >
                    {rangeLabelText}
                  </span>
                )}
                <div className="HasMaxWidth InputFieldWrapper">
                  <InputField
                    id={`${id}-max`}
                    name={`${id}-max`}
                    darkBg={darkBg}
                    type="number"
                    step={step}
                    disabled={disabled}
                    max={max}
                    onBlur={(e) => enforceMaxRange(e)}
                    value={formatNumber(
                      input2Value !== null ? input2Value : max
                    )}
                    {...input2Props}
                    onChange={onInputChange}
                    ref={input2Ref}
                    highlightInput={input2Active}
                    useHeadlineFont={true}
                  />
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        SliderSkeleton(max)
      )}
    </div>
  );
};

export default AASlider;
